import React, { useState, useEffect } from "react";
import { useGetBrandGuideItems, useDeleteBrandGuideItem } from "./hooks";
import { BrandGuideItem, brandGuideAPI } from "./api";
import { useI18n } from "../../i18n";

interface BrandGuideListProps {
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEditItem: (item: BrandGuideItem) => void;
}

const BrandGuideList: React.FC<BrandGuideListProps> = ({
  searchQuery,
  onSearchChange,
  onEditItem,
}) => {
  const { t, code } = useI18n();
  const { data: items, isLoading, error } = useGetBrandGuideItems(searchQuery);
  const deleteItemMutation = useDeleteBrandGuideItem();

  const handleEdit = (item: BrandGuideItem) => {
    onEditItem(item);
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm(t("brandGuide.confirmDelete"))
    ) {
      try {
        await deleteItemMutation.mutateAsync(id);
      } catch (error) {
        console.error("Error deleting brand guide item:", error);
      }
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const downloadUrl = await brandGuideAPI.getDownloadUrl(id);
      window.open(downloadUrl, "_blank");
    } catch (error) {
      alert(t("brandGuide.errors.downloadUrlFailed"));
      console.error("Download error:", error);
    }
  };

  const isImage = (fileKey: string) => {
    if (!fileKey) return false;
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".svg",
      ".webp",
      ".bmp",
    ];
    const lowercaseKey = fileKey.toLowerCase();
    return imageExtensions.some((ext) => lowercaseKey.includes(ext));
  };

  const ImagePreview: React.FC<{ item: BrandGuideItem }> = ({ item }) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (item.fileKey && isImage(item.fileKey)) {
        setLoading(true);
        brandGuideAPI
          .getPreviewUrl(item.id)
          .then((url) => {
            setPreviewUrl(url);
          })
          .catch((error) => {
            console.error("Failed to load preview:", error);
            setPreviewUrl(null);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }, [item.id, item.fileKey]);

    if (!item.fileKey) {
      return (
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
          <i className="fa-solid fa-file text-gray-400"></i>
        </div>
      );
    }

    if (isImage(item.fileKey) && previewUrl) {
      return (
        <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center">
          <img
            src={previewUrl}
            alt={item.title}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div class="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i class="fa-solid fa-file-image text-blue-600"></i>
                  </div>
                `;
              }
            }}
          />
        </div>
      );
    }

    if (isImage(item.fileKey)) {
      return (
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : (
            <i className="fa-solid fa-file-image text-blue-600"></i>
          )}
        </div>
      );
    }

    // For non-image files, show appropriate file icon
    const extension = item.fileKey.split(".").pop()?.toLowerCase();
    let iconClass = "fa-file";
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-600";

    switch (extension) {
      case "pdf":
        iconClass = "fa-file-pdf";
        bgColor = "bg-red-50";
        textColor = "text-red-600";
        break;
      case "doc":
      case "docx":
        iconClass = "fa-file-word";
        bgColor = "bg-blue-50";
        textColor = "text-blue-600";
        break;
      case "ppt":
      case "pptx":
        iconClass = "fa-file-powerpoint";
        bgColor = "bg-orange-50";
        textColor = "text-orange-600";
        break;
      default:
        iconClass = "fa-file";
        break;
    }

    return (
      <div
        className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center`}
      >
        <i className={`fa-solid ${iconClass} ${textColor}`}></i>
      </div>
    );
  };

  const renderPreview = (item: BrandGuideItem) => {
    return <ImagePreview item={item} />;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Logos & Brand Marks":
        return "fa-image";
      case "Usage Guidelines":
        return "fa-book";
      case "Templates & Assets":
        return "fa-palette";
      default:
        return "fa-file";
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
          <span className="ml-2 text-gray-600">
            {t("brandGuide.loading")}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4 mx-auto">
            <i className="fa-solid fa-exclamation-triangle text-red-400 text-2xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("brandGuide.loadFailed")}
          </h3>
          <p className="text-gray-500 text-sm">
            {t("common.somethingWentWrong")}
          </p>
        </div>
      </div>
    );
  }

  const groupedItems =
    items?.reduce(
      (acc: { [key: string]: BrandGuideItem[] }, item: BrandGuideItem) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
      },
      {}
    ) || {};

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Search and Controls */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1 w-full sm:max-w-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t("brandGuide.searchPlaceholder")}
                  value={searchQuery}
                  onChange={onSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <i className="fa-solid fa-search absolute left-3 top-2.5 text-gray-400"></i>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-gray-500">
              {t("common.itemsTotal", { count: items?.length || 0 })}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      disabled
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.item")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.category")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.date")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
                    <p className="text-gray-500">
                      {t("brandGuide.loading")}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          ) : !items || items.length === 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      disabled
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.item")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.category")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.date")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <i className="fa-solid fa-palette text-gray-400 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {searchQuery
                        ? t("brandGuide.empty.noResultsTitle")
                        : t("brandGuide.empty.emptyTitle")}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6">
                      {searchQuery
                        ? t("common.tryAdjustingSearch")
                        : t("brandGuide.empty.emptyBody")}
                    </p>
                    {!searchQuery && (
                      <div className="flex justify-center">
                        <button
                          onClick={() => {
                            // This will trigger the parent component to open the modal
                            const addButton = document.querySelector(
                              '[data-testid="add-brand-guide-btn"]'
                            ) as HTMLButtonElement;
                            if (addButton) addButton.click();
                          }}
                          className="px-4 py-2 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex items-center"
                        >
                          <i className="fa-solid fa-plus mr-2"></i>
                          {t("brandGuide.addFirstItem")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.item")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.category")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.date")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("brandGuide.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item: BrandGuideItem) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {renderPreview(item)}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {item.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {item.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {item.category}
                      </div>
                      <div className="text-sm text-gray-500">
                        {t("common.sortValue", { value: item.sortOrder })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.isActive ? t("common.active") : t("common.inactive")}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.createdAt).toLocaleDateString(code)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        {item.fileKey ? (
                          <button
                            onClick={() => handleDownload(item.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title={t("common.download")}
                          >
                            <i className="fa-solid fa-download"></i>
                          </button>
                        ) : (
                          <button
                            disabled
                            className="text-gray-400 cursor-not-allowed"
                            title={t("common.noFileAvailable")}
                          >
                            <i className="fa-solid fa-download"></i>
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title={t("common.edit")}
                        >
                          <i className="fa-solid fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          disabled={deleteItemMutation.isPending}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                          title={t("common.delete")}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default BrandGuideList;
